(() => {
  // Debug flag (check in console)
  window.__personalisationLoaded = true;

  const SELECTORS = {
    openBtn: '[data-personalisation-open], button[aria-controls^="personalisation-modal-"]',
    modal: '[data-personalisation-modal]',
    closeBtn: '[data-personalisation-close]',
    addBtn: '[data-personalisation-add]',

    initialsInput: '[data-personalisation-initials]',
    count: '[data-personalisation-count]',
    swatches: 'input[name="personalisation_colour"]',

    previewText: '[data-personalisation-preview-text]',
    previewImage: '[data-personalisation-preview-image]',

    price: '[data-personalisation-price]',
    selectedColourLabel: '[data-personalisation-selected-colour]',
    confirm: '[data-personalisation-confirm]',

    // main product hidden properties (optional)
    propEnabled: '[data-personalisation-prop-enabled]',
    propInitials: '[data-personalisation-prop-initials]',
    propColour: '[data-personalisation-prop-colour]',

    // preview stage (for scaling)
    stage: '.PersonalisationPreview__Stage',
  };

  const qs = (sel, root = document) => root.querySelector(sel);

  // Accessibility focus restore
  let lastFocusedEl = null;

  // One ResizeObserver per modal
  const __stageObservers = new WeakMap();

  // ----------------------------
  // Character rules
  // ----------------------------
  const MAX_LENGTH = 6;

  // Allowed punctuation + max count per string
  const LIMITED_PUNCTUATION = {
    '.': 3,
    ',': 2,
    '‘': 1, // curly apostrophe
    '–': 1, // en dash
    '?': 1,
    '!': 1,
    '&': 1,
  };

  function isLetterOrNumber(ch) {
    return /^[A-Z0-9]$/.test(ch);
  }

  function filterAndLimitInitials(value) {
    const raw = (value || '').toString().toUpperCase();

    let result = '';
    const punctuationCounts = {};

    for (const ch of raw) {
      if (result.length >= MAX_LENGTH) break;

      if (isLetterOrNumber(ch)) {
        result += ch;
        continue;
      }

      if (LIMITED_PUNCTUATION[ch]) {
        punctuationCounts[ch] = punctuationCounts[ch] || 0;

        if (punctuationCounts[ch] < LIMITED_PUNCTUATION[ch]) {
          punctuationCounts[ch] += 1;
          result += ch;
        }
      }
    }

    return result;
  }

  // ----------------------------
  // Helpers
  // ----------------------------
  function normaliseInitials(v) {
    return filterAndLimitInitials(v);
  }

  function getSelectedColour(modal) {
    const checked = modal?.querySelector('input[name="personalisation_colour"]:checked');
    return (checked?.value || 'Blind').toString().trim();
  }

  function updateSelectedColourLabel(modal) {
    if (!modal) return;
    const el = qs(SELECTORS.selectedColourLabel, modal);
    if (!el) return;
    el.textContent = ` ${getSelectedColour(modal)}`;
  }

  function setPreviewColour(modal, colour) {
    const preview = qs(SELECTORS.previewText, modal);
    if (preview) preview.dataset.colour = colour;
  }

  function updatePreview(modal) {
    const input = qs(SELECTORS.initialsInput, modal);
    const count = qs(SELECTORS.count, modal);
    const preview = qs(SELECTORS.previewText, modal);

    const v = normaliseInitials(input?.value);
    if (preview) preview.textContent = v;
    if (count) count.textContent = `${v.length}/${MAX_LENGTH}`;
  }

  function resetModal(modal) {
    if (!modal) return;

    // initials
    const input = qs(SELECTORS.initialsInput, modal);
    if (input) input.value = '';

    // swatches -> first checked
    const radios = modal.querySelectorAll(SELECTORS.swatches);
    if (radios && radios.length) {
      radios.forEach((r) => (r.checked = false));
      radios[0].checked = true;
    }

    // preview text
    const preview = qs(SELECTORS.previewText, modal);
    if (preview) preview.textContent = '';

    setPreviewColour(modal, 'Blind');
    updateSelectedColourLabel(modal);
    updatePreview(modal);
  }

  function openModal(modal) {
    if (!modal) return;

    lastFocusedEl = document.activeElement;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('no-scroll');

    // focus initials for UX
    const input = qs(SELECTORS.initialsInput, modal);
    if (input) input.focus();
  }

  function closeModal(modal) {
    if (!modal) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('no-scroll');

    // restore focus
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      lastFocusedEl.focus();
    }
  }

  function resolveModalFromTrigger(trigger) {
    if (!trigger) return null;

    const id = trigger.getAttribute('aria-controls');
    if (id) {
      const byId = document.getElementById(id);
      if (byId) return byId;
    }
    return qs(SELECTORS.modal);
  }

  function syncPriceFromTrigger(modal, trigger) {
    if (!modal || !trigger) return;
    const priceEl = qs(SELECTORS.price, modal);
    if (priceEl) priceEl.textContent = trigger.dataset.personalisationPriceMoney || '';
  }

  function syncPreviewImageFromTrigger(modal, trigger) {
    if (!modal || !trigger) return;

    // Parse variant->image map from open button
    let map = {};
    try {
      map = JSON.parse(trigger.dataset.personalisationVariantImages || '{}');
    } catch (e) {
      console.warn('[personalisation] could not parse variant image map', e);
      map = {};
    }

    // Find the live selected variant id from product form
    const productFormEl =
      trigger.closest('product-form') ||
      document.querySelector('product-form') ||
      document.querySelector('form[action*="/cart/add"]');

    const idInput =
      productFormEl?.querySelector('input[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      document.querySelector('input[name="id"]');

    const variantId = idInput?.value;
    const nextSrc = variantId && map[variantId] ? map[variantId] : null;

    if (!nextSrc) {
      console.warn('[personalisation] no image found for variant', {
        variantId,
        mapKeys: Object.keys(map || {}),
      });
      return;
    }

    const img = qs(SELECTORS.previewImage, modal);
    if (img) {
      img.src = nextSrc;
      img.setAttribute('src', nextSrc);
    }
  }

  // ----------------------------
  // Scaling (writes --p-fs-scaled and --p-ls-scaled on stage)
  // ----------------------------
  function updateStageScale(modal) {
    const stage = modal?.querySelector(SELECTORS.stage);
    const img = qs(SELECTORS.previewImage, modal);
    const preview = modal?.querySelector('.PersonalisationPreview'); // where --p-fs lives
    if (!stage || !img || !preview) return;

    const naturalW = img.naturalWidth || 0;
    const stageW = stage.clientWidth || 0;
    if (!naturalW || !stageW) return;

    const scale = stageW / naturalW;

    const fsRaw = getComputedStyle(preview).getPropertyValue('--p-fs').trim(); // e.g. "40px"
    const baseFs = parseFloat(fsRaw) || 22;

    const lsRaw = getComputedStyle(preview).getPropertyValue('--p-ls').trim(); // e.g. "0.08em"
    const baseLsEm = parseFloat(lsRaw) || 0.08;

    const fsScaledPx = baseFs * scale;
    const lsScaledPx = fsScaledPx * baseLsEm;

    stage.style.setProperty('--p-scale', String(scale));
    stage.style.setProperty('--p-fs-scaled', `${fsScaledPx}px`);
    stage.style.setProperty('--p-ls-scaled', `${lsScaledPx}px`);
  }

  function ensureStageResizeObserver(modal) {
    const stage = modal?.querySelector(SELECTORS.stage);
    const img = qs(SELECTORS.previewImage, modal);
    if (!stage || !img) return;

    if (__stageObservers.has(modal)) {
      updateStageScale(modal);
      return;
    }

    const ro = new ResizeObserver(() => updateStageScale(modal));
    ro.observe(stage);

    img.addEventListener('load', () => updateStageScale(modal));

    __stageObservers.set(modal, ro);

    updateStageScale(modal);
  }

  // ----------------------------
  // Hidden props on main form (optional)
  // ----------------------------
  function writeHiddenProps(initials, colour, enabled) {
    const propEnabled = qs(SELECTORS.propEnabled);
    const propInitials = qs(SELECTORS.propInitials);
    const propColour = qs(SELECTORS.propColour);

    if (propEnabled) propEnabled.value = enabled ? 'Yes' : 'No';
    if (propInitials) propInitials.value = enabled ? initials : '';
    if (propColour) propColour.value = enabled ? colour : '';
  }

  // ----------------------------
  // Network helpers
  // ----------------------------
  async function fetchJson(url, opts = {}) {
    const res = await fetch(url, {
      ...opts,
      headers: {
        Accept: 'application/json',
        ...(opts.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw data;
    return data;
  }

  function tryOpenCartDrawer() {
    // best-effort across themes
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:open'));

    const selectors = [
      '[data-drawer-trigger="cart"]',
      '[data-cart-drawer-open]',
      '[aria-controls*="cart"]',
      'a[href="/cart"]',
    ];

    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) {
        try {
          el.click();
          return true;
        } catch (e) {}
      }
    }
    return false;
  }

  // ----------------------------
  // Main click handler (delegated)
  // ----------------------------
  document.addEventListener(
    'click',
    async (e) => {
      // OPEN
      const openTrigger = e.target.closest(SELECTORS.openBtn);
      if (openTrigger) {
        const modal = resolveModalFromTrigger(openTrigger);
        if (!modal) return;

        // stash data on modal so ADD handler doesn't need trigger
        modal.dataset.addonVariantMap = openTrigger.dataset.personalisationAddonVariantMap || '';
        modal.dataset.forProductLabel = openTrigger.dataset.personalisationForProductLabel || '';

        resetModal(modal);

        syncPriceFromTrigger(modal, openTrigger);
        syncPreviewImageFromTrigger(modal, openTrigger);

        openModal(modal);
        ensureStageResizeObserver(modal);

        return;
      }

      // CLOSE
      const close = e.target.closest(SELECTORS.closeBtn);
      if (close) {
        const modal = close.closest(SELECTORS.modal) || qs(SELECTORS.modal);
        writeHiddenProps('', '', false);
        closeModal(modal);
        return;
      }

      // ADD
      const add = e.target.closest(SELECTORS.addBtn);
      if (add) {
        const modal = add.closest(SELECTORS.modal);
        if (!modal) return;

        const initials = normaliseInitials(qs(SELECTORS.initialsInput, modal)?.value);
        const colour = getSelectedColour(modal);

        // require confirm checkbox if present
        const confirm = qs(SELECTORS.confirm, modal);
        if (confirm && !confirm.checked) {
          alert('Please confirm personalised items are non-returnable.');
          return;
        }

        // Main variant id from the live product form
        const productFormEl =
          document.querySelector('product-form') ||
          document.querySelector('form[action*="/cart/add"]');

        const idInput =
          productFormEl?.querySelector('input[name="id"]') ||
          document.querySelector('input[name="id"]');

        const mainVariantId = idInput?.value ? Number(idInput.value) : null;
        if (!mainVariantId) {
          console.warn('[personalisation] Could not find main variant id');
          return;
        }

        // Resolve add-on variant from the map stored on modal
        let map = {};
        try {
          map = JSON.parse(modal.dataset.addonVariantMap || '{}');
        } catch (e) {
          console.warn('[personalisation] bad add-on map JSON', modal.dataset.addonVariantMap);
          map = {};
        }

        const addonVariantIdRaw = map[String(colour || '').trim()];
        const addonVariantId = addonVariantIdRaw ? Number(addonVariantIdRaw) : null;

        if (!addonVariantId) {
          console.warn('[personalisation] Could not resolve add-on variant for selected colour', {
            colour,
            checked: modal?.querySelector('input[name="personalisation_colour"]:checked')?.value,
            mapKeys: Object.keys(map || {}),
            mapRaw: modal.dataset.addonVariantMap,
          });
          return;
        }

        // update main hidden props
        writeHiddenProps(initials, colour, true);

        // Add both items in one request
        const payload = {
          items: [
            {
              id: addonVariantId,
              quantity: 1,
              properties: {
                'For product': modal.dataset.forProductLabel || window.location.pathname,
                __personalisation_key: String(Date.now()) + '-' + Math.random().toString(16).slice(2),
              },
            },
            {
              id: mainVariantId,
              quantity: 1,
              properties: {
                Personalisation: 'Yes',
                Initials: initials,
                'Embossing colour': colour,
                ...(modal.dataset.personalisationLeadTimeNote
                ? { 'Lead time': modal.dataset.personalisationLeadTimeNote }
                : {}),
              },
            },
          ],
        };

        try {
          console.log('payload being sent', payload)

          await fetchJson('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          closeModal(modal);

          const opened = tryOpenCartDrawer();
          if (!opened) window.location.href = '/cart';
        } catch (err) {
          console.error('[personalisation] add to cart failed', err);
          alert(err?.description || err?.message || 'Could not add to cart. Please try again.');
        }

        return;
      }
    },
    true
  );

  // initials input updates preview and enforces allowed chars live
  document.addEventListener('input', (e) => {
    const modal = e.target.closest(SELECTORS.modal);
    if (!modal) return;

    if (e.target.matches(SELECTORS.initialsInput)) {
      const input = e.target;
      input.value = filterAndLimitInitials(input.value);
      updatePreview(modal);
    }
  });

  // swatch change updates preview colour + label
  document.addEventListener('change', (e) => {
    const modal = e.target.closest(SELECTORS.modal);
    if (!modal) return;

    if (e.target.matches(SELECTORS.swatches)) {
      const colour = getSelectedColour(modal);
      setPreviewColour(modal, colour);
      updateSelectedColourLabel(modal);
    }
  });

  // ESC closes
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const openModalEl = Array.from(document.querySelectorAll(SELECTORS.modal)).find((m) => m.hidden === false);
    if (openModalEl) closeModal(openModalEl);
  });

  console.log('[personalisation] loaded ✅', { loadedFlag: window.__personalisationLoaded });
})();